import { Type } from 'class-transformer';

import { IPagination, IQueryPaginationDto } from '@/types/utils';

// Device Status Types
export class DeviceStatus {
  id: string;
  name: string;
  description?: string;
  colorCode?: string;

  @Type(() => Date)
  createdAt?: Date;

  @Type(() => Date)
  updatedAt?: Date;
}

export class IDeviceStatusResponse {
  @Type(() => DeviceStatus)
  result: DeviceStatus;
}

export class IDeviceStatusListResponse extends IPagination {
  @Type(() => DeviceStatus)
  results: DeviceStatus[];
}

export class IDeviceStatusQueryDto extends IQueryPaginationDto {
  name?: string;
}

export class IPostDeviceStatusBodyDto {
  name: string;
  description?: string;
  colorCode?: string;
}

// Device Type Types
export class DeviceType {
  id: string;
  name: string;
  description?: string;

  @Type(() => Date)
  createdAt?: Date;

  @Type(() => Date)
  updatedAt?: Date;
}

export class IDeviceTypeResponse {
  @Type(() => DeviceType)
  result: DeviceType;
}

export class IDeviceTypeListResponse extends IPagination {
  @Type(() => DeviceType)
  results: DeviceType[];
}

export class IDeviceTypeQueryDto extends IQueryPaginationDto {
  name?: string;
}

export class IPostDeviceTypeBodyDto {
  name: string;
  description?: string;
}

// Brand Types
export class Brand {
  id: string;
  name: string;
  description?: string;

  @Type(() => Date)
  createdAt?: Date;

  @Type(() => Date)
  updatedAt?: Date;
}

export class IBrandResponse {
  @Type(() => Brand)
  result: Brand;
}

export class IBrandListResponse extends IPagination {
  @Type(() => Brand)
  results: Brand[];
}

export class IBrandQueryDto extends IQueryPaginationDto {
  name?: string;
}

export class IPostBrandBodyDto {
  name: string;
  description?: string;
}

// Device Model Types
export class DeviceModel {
  id: string;
  brandId: string | Brand; // API can return string or populated Brand object
  name: string;
  description?: string;

  @Type(() => Brand)
  brand?: Brand;

  @Type(() => Date)
  createdAt?: Date;

  @Type(() => Date)
  updatedAt?: Date;

  // Getter to handle both brandId as string or object
  get brandData(): Brand | undefined {
    if (typeof this.brandId === 'object') {
      return this.brandId as Brand;
    }
    return this.brand;
  }
}

export class IDeviceModelResponse {
  @Type(() => DeviceModel)
  result: DeviceModel;
}

export class IDeviceModelListResponse extends IPagination {
  @Type(() => DeviceModel)
  results: DeviceModel[];
}

export class IDeviceModelQueryDto extends IQueryPaginationDto {
  brandId?: string;
  name?: string;
}

export class IPostDeviceModelBodyDto {
  brandId: string;
  name: string;
  description?: string;
}

// Inventory Log Types
export type InventoryLogAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'STATUS_CHANGE' | 'ASSIGN' | 'UNASSIGN' | 'TRANSFER';
export type InventoryLogEntityType = 'INVENTORY' | 'DEVICE_STATUS' | 'DEVICE_TYPE' | 'BRAND' | 'MODEL';

export class InventoryLog {
  id: string;
  userId: number;
  action: InventoryLogAction;
  entityType: InventoryLogEntityType;
  entityId: string;
  oldData?: Record<string, unknown>;
  newData?: Record<string, unknown>;
  description?: string;

  @Type(() => Date)
  createdAt: Date;
}

export class IInventoryLogResponse {
  @Type(() => InventoryLog)
  result: InventoryLog;
}

export class IInventoryLogListResponse extends IPagination {
  @Type(() => InventoryLog)
  results: InventoryLog[];
}

export class IInventoryLogQueryDto extends IQueryPaginationDto {
  userId?: number;
  action?: InventoryLogAction;
  entityType?: InventoryLogEntityType;
  entityId?: string | number;
}

// Inventory Types - API Schema
export class IPostInventoryBodyDto {
  deviceTypeId: string;
  statusId: string;
  brandId: string;
  modelId: string;
  tip: 'talep' | 'envanter';
  hafiza?: string;
  imei1?: string;
  imei2?: string;
  serino?: string;
  aciklama?: string;
  userId?: number;
  zimmetTarihi?: Date | string;
  pointId?: number;
  purchaseDate?: Date | string;
  deviceName?: string;
  phoneNumber?: string;
  companyId?: number;
  sendDate?: Date | string;
  cityIdNo?: string;
  urunStokGirisiTarihi?: Date | string;
  cityId?: number;
  subCityId?: number;
}

export class Inventory {
  id: string;
  deviceTypeId: string;
  statusId: string;
  brandId: string;
  modelId: string;
  tip: string;
  hafiza?: string;
  imei1?: string;
  imei2?: string;
  serino?: string;
  aciklama?: string;
  userId?: number;
  pointId?: number;
  deviceName?: string;
  phoneNumber?: string;
  companyId?: number;
  cityIdNo?: string;
  cityId?: number;
  subCityId?: number;

  @Type(() => Date)
  zimmetTarihi?: Date;

  @Type(() => Date)
  purchaseDate?: Date;

  @Type(() => Date)
  sendDate?: Date;

  @Type(() => Date)
  urunStokGirisiTarihi?: Date;

  @Type(() => Date)
  createdAt?: Date;

  @Type(() => Date)
  updatedAt?: Date;

  // Nested objects from API
  @Type(() => DeviceType)
  deviceType?: DeviceType;

  @Type(() => DeviceStatus)
  status?: DeviceStatus;

  @Type(() => Brand)
  brand?: Brand;

  @Type(() => DeviceModel)
  model?: DeviceModel;

  user?: Record<string, unknown>; // User type if available
  point?: Record<string, unknown>; // Point type if available
  company?: Record<string, unknown>; // Company type if available
}

export class IInventoryResponse {
  @Type(() => Inventory)
  result: Inventory;
}

export class IInventoryListResponse extends IPagination {
  @Type(() => Inventory)
  results: Inventory[];
}

export class IInventoryQueryDto extends IQueryPaginationDto {
  deviceTypeId?: string;
  deviceTypeIds?: string[];
  statusId?: string;
  brandId?: string;
  brandIds?: string[];
  modelId?: string;
  tip?: 'talep' | 'envanter';
  userId?: number;
  pointId?: number;
  pointIds?: number[];
  companyId?: number;
  companyIds?: number[];
  cityIdNo?: string;
  phoneNumber?: string;
  imei1?: string;
  serino?: string;
  deviceName?: string;
}
